import { useState } from 'react';
import { Percent, Award, Tag, X, Calendar, Building2 } from 'lucide-react';
import { ESQUEMAS_PAGO } from '@/constants/catalogo';
import { SUBMARCAS } from '@/constants/brand';
import { fmt, determinarSubmarca } from '@/lib/utils';

export default function StepDescuentos({ quote, setQuote, discountCodes }) {
  const [codigoInput, setCodigoInput] = useState('');
  const [codigoError, setCodigoError] = useState('');

  const aplicarCodigo = () => {
    const codigo = discountCodes.find(
      (c) => c.codigo.toLowerCase() === codigoInput.toLowerCase() && c.activo
    );
    if (!codigo) {
      setCodigoError('Código no válido o inactivo');
      return;
    }
    if (quote.descuentos.codigos.find((c) => c.id === codigo.id)) {
      setCodigoError('Este código ya está aplicado');
      return;
    }
    setQuote((q) => ({
      ...q,
      descuentos: { ...q.descuentos, codigos: [...q.descuentos.codigos, codigo] },
    }));
    setCodigoInput('');
    setCodigoError('');
  };

  const removerCodigo = (id) => {
    setQuote((q) => ({
      ...q,
      descuentos: {
        ...q.descuentos,
        codigos: q.descuentos.codigos.filter((c) => c.id !== id),
      },
    }));
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-white mb-1">
        Descuentos y esquema de pago
      </h2>
      <p className="text-sm text-neutral-500 mb-6">
        Ajusta los descuentos aplicables y define cómo se cobrará el proyecto.
      </p>

      <div className="space-y-5">
        {/* Descuentos */}
        <div className="p-5 bg-neutral-900/50 border border-neutral-800 rounded-xl">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Percent className="w-4 h-4 text-brand-gold" /> Descuentos aplicables
          </h3>

          <div
            className={`p-3 rounded-lg border mb-3 ${
              quote.descuentos.bundle
                ? 'border-brand-gold/50 bg-brand-gold/5'
                : 'border-neutral-800 bg-neutral-900/50'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-brand-gold" />
                <div>
                  <div className="text-sm text-white">Bundle Full Xperience</div>
                  <div className="text-xs text-neutral-500">
                    Auto-aplicable con 4+ productos
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {quote.descuentos.bundle ? (
                  <span className="text-green-400 text-xs">Aplicado</span>
                ) : (
                  <span className="text-neutral-500 text-xs">No aplica</span>
                )}
                <span className="text-brand-gold font-medium text-sm">
                  {quote.descuentos.bundlePct}%
                </span>
              </div>
            </div>
          </div>

          <label className="flex items-center justify-between p-3 bg-neutral-900/50 border border-neutral-800 rounded cursor-pointer hover:border-neutral-700 mb-3">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={quote.descuentos.elementosPrevios}
                onChange={(e) =>
                  setQuote((q) => ({
                    ...q,
                    descuentos: { ...q.descuentos, elementosPrevios: e.target.checked },
                  }))
                }
                className="accent-brand-gold"
              />
              <div>
                <div className="text-sm text-white">
                  Cliente trae nombre y logo definidos
                </div>
                <div className="text-xs text-neutral-500">
                  Aplica solo si hay Identidad de Marca en la cotización
                </div>
              </div>
            </div>
            <span className="text-brand-gold text-sm font-medium">5%</span>
          </label>

          <div>
            <label className="text-xs text-neutral-400 uppercase tracking-wider mb-2 block">
              Código promocional
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={codigoInput}
                onChange={(e) => {
                  setCodigoInput(e.target.value);
                  setCodigoError('');
                }}
                placeholder="ej. PROPTECH2026"
                onKeyDown={(e) => e.key === 'Enter' && aplicarCodigo()}
                className="flex-1 px-3 py-2 bg-black/40 border border-neutral-800 rounded text-white text-sm focus:outline-none focus:border-brand-gold/50 uppercase"
              />
              <button
                onClick={aplicarCodigo}
                className="px-4 py-2 bg-brand-gold text-black rounded text-sm font-medium hover:bg-yellow-400"
              >
                Aplicar
              </button>
            </div>
            {codigoError && (
              <div className="text-red-400 text-xs mt-1">{codigoError}</div>
            )}

            {quote.descuentos.codigos.length > 0 && (
              <div className="mt-3 space-y-2">
                {quote.descuentos.codigos.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between p-2 bg-green-500/5 border border-green-500/30 rounded"
                  >
                    <div className="flex items-center gap-2">
                      <Tag className="w-3 h-3 text-green-500" />
                      <span className="text-xs text-white font-medium">{c.codigo}</span>
                      <span className="text-xs text-neutral-500">{c.nombre}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-400 text-xs font-medium">
                        -{c.descuento}%
                      </span>
                      <button
                        onClick={() => removerCodigo(c.id)}
                        className="text-neutral-500 hover:text-red-400"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-4 pt-4 border-t border-neutral-800 flex items-center justify-between">
            <div className="text-xs text-neutral-500">Descuento total aplicado</div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-neutral-500">(tope 25%)</span>
              <span className="text-green-400 font-bold">{quote.descuentoPct}%</span>
            </div>
          </div>
        </div>

        {/* Licencia prepagada */}
        <div
          className={`p-5 rounded-xl border ${
            quote.pago.licenciaPrepagada
              ? 'border-brand-gold/50 bg-brand-gold/5'
              : 'border-neutral-800 bg-neutral-900/50'
          }`}
        >
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={quote.pago.licenciaPrepagada}
              onChange={(e) =>
                setQuote((q) => ({
                  ...q,
                  pago: { ...q.pago, licenciaPrepagada: e.target.checked },
                }))
              }
              className="accent-brand-gold mt-1"
            />
            <div className="flex-1">
              <div className="flex items-center justify-between mb-1">
                <div className="text-sm text-white font-medium">
                  Licencia prepagada años 1-3
                </div>
                <div className="text-brand-gold font-medium">
                  +{fmt(quote.pago.licenciaPrepagadaPrecio)}
                </div>
              </div>
              <div className="text-xs text-neutral-500">
                Cobra upfront los costos de hosting, servidores y mantenimiento que
                absorbes durante los primeros 3 años. Opcional pero recomendado.
              </div>
            </div>
          </label>
        </div>

        {/* Esquema de pago */}
        <div className="p-5 bg-neutral-900/50 border border-neutral-800 rounded-xl">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-brand-gold" /> Esquema de pago
          </h3>
          <select
            value={quote.pago.esquema}
            onChange={(e) =>
              setQuote((q) => ({ ...q, pago: { ...q.pago, esquema: e.target.value } }))
            }
            className="w-full px-4 py-2.5 bg-black/40 border border-neutral-800 rounded-lg text-white focus:outline-none focus:border-brand-gold/50 text-sm mb-3"
          >
            {ESQUEMAS_PAGO.map((e) => (
              <option key={e.id} value={e.id}>
                {e.label}
              </option>
            ))}
          </select>
          <textarea
            value={quote.pago.notas}
            onChange={(e) =>
              setQuote((q) => ({ ...q, pago: { ...q.pago, notas: e.target.value } }))
            }
            placeholder="Notas sobre condiciones de pago específicas para este cliente..."
            rows={2}
            className="w-full px-3 py-2 bg-black/40 border border-neutral-800 rounded text-white text-sm focus:outline-none focus:border-brand-gold/50 resize-none"
          />
        </div>

        {/* Submarca emisora */}
        <div className="p-5 bg-neutral-900/50 border border-neutral-800 rounded-xl">
          <h3 className="text-white font-medium mb-4 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-brand-gold" /> Submarca emisora
          </h3>
          <div className="text-xs text-neutral-500 mb-3">
            Detectada automáticamente según productos:{' '}
            <span className="text-brand-gold font-medium">
              {SUBMARCAS[determinarSubmarca(quote.productos)]?.name}
            </span>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={quote.submarcaManual}
              onChange={(e) =>
                setQuote((q) => ({ ...q, submarcaManual: e.target.checked }))
              }
              className="accent-brand-gold"
            />
            <span className="text-sm text-neutral-400">Seleccionar manualmente</span>
          </label>
          {quote.submarcaManual && (
            <div className="grid grid-cols-3 gap-2 mt-3">
              {Object.entries(SUBMARCAS).map(([k, s]) => (
                <button
                  key={k}
                  onClick={() => setQuote((q) => ({ ...q, submarca: k }))}
                  className={`p-3 rounded border text-left ${
                    quote.submarca === k
                      ? 'border-brand-gold bg-brand-gold/10'
                      : 'border-neutral-800 bg-neutral-900/50'
                  }`}
                >
                  <div className="text-white text-xs font-medium">{s.name}</div>
                  <div
                    className="w-full h-0.5 rounded-full mt-2"
                    style={{ background: s.accent }}
                  />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Ajuste manual */}
        <div className="p-5 bg-neutral-900/50 border border-neutral-800 rounded-xl">
          <h3 className="text-white font-medium mb-3">
            Ajuste manual al total (opcional)
          </h3>
          <div className="flex items-center gap-3">
            <span className="text-sm text-neutral-400">Cantidad (+/-)</span>
            <input
              type="number"
              value={quote.ajusteManual || 0}
              onChange={(e) =>
                setQuote((q) => ({
                  ...q,
                  ajusteManual: parseFloat(e.target.value) || 0,
                }))
              }
              className="flex-1 px-3 py-2 bg-black/40 border border-neutral-800 rounded text-white text-sm focus:outline-none focus:border-brand-gold/50"
            />
          </div>
          <div className="text-xs text-neutral-500 mt-2">
            Útil para redondeos, negociaciones especiales o ajustes caso-por-caso
          </div>
        </div>

        {/* Resumen final */}
        <div className="p-6 bg-gradient-to-br from-brand-gold/10 to-brand-gold/5 border border-brand-gold/30 rounded-xl">
          <h3 className="text-white font-semibold mb-4">Resumen financiero</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-neutral-400">
              <span>Subtotal productos</span>
              <span>
                {fmt(quote.productos.reduce((s, p) => s + (p.precioFinal || 0), 0))}
              </span>
            </div>
            {quote.pago.licenciaPrepagada && (
              <div className="flex justify-between text-neutral-400">
                <span>Licencia prepagada 3 años</span>
                <span>+{fmt(quote.pago.licenciaPrepagadaPrecio)}</span>
              </div>
            )}
            <div className="flex justify-between text-neutral-400 pt-2 border-t border-brand-gold/20">
              <span>Subtotal</span>
              <span>{fmt(quote.subtotal)}</span>
            </div>
            {quote.descuentoPct > 0 && (
              <div className="flex justify-between text-green-400">
                <span>Descuento ({quote.descuentoPct}%)</span>
                <span>-{fmt(quote.descuentoTotal)}</span>
              </div>
            )}
            {quote.ajusteManual !== 0 && (
              <div className="flex justify-between text-neutral-400">
                <span>Ajuste manual</span>
                <span>
                  {quote.ajusteManual > 0 ? '+' : ''}
                  {fmt(quote.ajusteManual)}
                </span>
              </div>
            )}
            <div className="flex justify-between text-white text-2xl font-bold pt-3 border-t border-brand-gold/30">
              <span>Total</span>
              <span>{fmt(quote.total)}</span>
            </div>
            <div className="text-xs text-neutral-500 text-right">+ IVA</div>
          </div>
        </div>
      </div>
    </div>
  );
}
